import FormInput from "../inputs/FormInput.tsx";
import { InputType } from "../../types/input.ts";
import CheckboxInput from "../inputs/CheckboxInput.tsx";
import Button from "../buttons/Button.tsx";
import { ButtonSize, ButtonType, ButtonVariant } from "../../types/button.ts";
import React, { ChangeEvent, FormEvent, useState } from "react";
import { useAccount } from "../../hooks/useAccount.tsx";
import ValidatorsHelper from "../../helpers/validatorsHelper.ts";
import { BadRequestMessageStatusTypes } from "../../exceptions/badRequestException.ts";
import { NotFoundMessageStatusTypes } from "../../exceptions/notFoundException.ts";
import { useNotification } from "../../providers/NotificationProvider.tsx";
import { NotificationStatus } from "../notification/Notification.tsx";
import { useAuth } from "../../hooks/useAuth.tsx";

const AccountChangePasswordForm = () => {
    const _accountService = useAccount();
    const _authService = useAuth();
    const _notificationService = useNotification();
    const [showErrors, setShowErrors] = useState(false);
    const [input, setInput] = useState({
        actualPassword: "",
        newPassword: "",
        confirmPassword: "",
        logoutFromAllDevices: true
    });
    const [inputValid, setInputValid] = useState({
        newPasswordIsValid: false,
        confirmPasswordIsValid: false,
    })
    const [actualPasswordIsIncorrect, setActualPasswordIsIncorrect] = useState(false);
    const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({
            ...prev,
            newPassword: e.target.value
        }));
        setInputValid({
            newPasswordIsValid: ValidatorsHelper.isPasswordValid(e.target.value),
            confirmPasswordIsValid: e.target.value === input.confirmPassword
        });
    }

    const handleActualPasswordInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({ ...prev, actualPassword: e.target.value }))
    }

    const handleConfirmPasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({
            ...prev,
            confirmPassword: e.target.value
        }));
        setInputValid({
            newPasswordIsValid: ValidatorsHelper.isPasswordValid(input.newPassword),
            confirmPasswordIsValid: e.target.value === input.newPassword
        });
    }
    const handleLogoutFromAllDevicesCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
        setInput(prev => ({ ...prev, logoutFromAllDevices: e.target.checked }));
    }

    const handleSubmitEvent = async (e: FormEvent) => {
        e.preventDefault();
        if (inputValid.newPasswordIsValid && inputValid.confirmPasswordIsValid) {
            try {
                await _accountService.changePassword(input.actualPassword, input.newPassword, input.confirmPassword, input.logoutFromAllDevices);
                setInput({ actualPassword: "", newPassword: "", confirmPassword: "", logoutFromAllDevices: true });
                setShowErrors(false);
                setInputValid({ newPasswordIsValid: false, confirmPasswordIsValid: false })
                setActualPasswordIsIncorrect(false);
                if (input.logoutFromAllDevices)
                    await _authService.logoutFromAllDevices();
            } catch (e) {
                const error = e as { code: number, status: BadRequestMessageStatusTypes | NotFoundMessageStatusTypes };
                if (error.status === "PASSWORD_IS_INCORRECT") {
                    setActualPasswordIsIncorrect(true);
                    _notificationService.showNotification("Zmiana hasła niepowiodła się", "Aktualne hasło jest nieprawidłowe.", NotificationStatus.failure);
                }
            }
        } else {
            setShowErrors(true);
        }
    }
    return (
        <form action="/account/change-password"
            className={"w-full text-left animate-opacityEaseIn"}
            onSubmit={(e) => handleSubmitEvent(e)}
        >
            <span className={"flex flex-col space-y-5"}>
                <span>
                    <FormInput
                        label={"Obecne hasło"}
                        name={"password"}
                        onInputChange={handleActualPasswordInputChange}
                        type={InputType.PASSWORD}
                        value={input.actualPassword}
                        placeholder={"Wpisz obecne hasło"}
                        className={"bg-neutral-600 ring-offset-neutral-900 py-2"}
                    />
                    {showErrors && actualPasswordIsIncorrect &&
                        <p className={"text-red-500 mt-2"}>Hasło jest nieprawidłowe</p>}
                </span>
                <span>
                    <FormInput
                        label={"Hasło"}
                        name={"password"}
                        onInputChange={handlePasswordInputChange}
                        type={InputType.PASSWORD}
                        value={input.newPassword}
                        placeholder={"Wpisz nowe hasło"}
                        className={"bg-neutral-600 ring-offset-neutral-900 py-2"}
                    />
                    {showErrors && !inputValid.newPasswordIsValid &&
                        <p className={"text-red-500 mt-2 font-light"}>
                            <span className={"font-normal"}>Hasło musi zawierać:</span> <br />
                            - minimum 8 znaków <br />
                            - znak specjalny <br />
                            - dużą literę <br />
                            - cyfrę</p>}
                </span>
                <span>
                    <FormInput
                        label={"Potwierdź hasło"}
                        name={"confirmPassword"}
                        onInputChange={handleConfirmPasswordInputChange}
                        type={InputType.PASSWORD}
                        value={input.confirmPassword}
                        placeholder={"Potwierdź nowe hasło"}
                        className={"bg-neutral-600 ring-offset-neutral-900 py-2"}
                    />
                    {showErrors && !inputValid.confirmPasswordIsValid &&
                        <p className={"text-red-500 mt-2"}>Hasło i powtórz hasło różnią się od siebie</p>}
                </span>
                <CheckboxInput
                    isChecked={input.logoutFromAllDevices}
                    label={"Wyloguj ze wszystkich urządzeń"}
                    name={"logoutFromAllDevices"}
                    onChange={handleLogoutFromAllDevicesCheckbox} />
            </span>
            <Button
                type={ButtonType.SUBMIT}
                size={ButtonSize.BASE}
                variant={ButtonVariant.PRIMARY}
                className={"mx-auto block w-full ring-offset-neutral-800 py-1.5 mt-6 text-lg"}>Wyslij</Button>

        </form>
    );
};

export default AccountChangePasswordForm;